import type { Infer } from 'spacetimedb';
import { GameStateRow, type EventContext } from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type {
  GameTickSubscription,
  GameTickSubscriptionOptions,
  Logger
} from '../types';

type GameStateTableRow = Infer<typeof GameStateRow>;

export class GameTickModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly logger: Logger = () => undefined
  ) {}

  public async subscribeToGameTick(options: GameTickSubscriptionOptions): Promise<GameTickSubscription> {
    const connection = await this.connectionManager.ensureConnection();
    const gameStateTable = connection.db.gameState;

    const emitTick = (rows: Iterable<GameStateTableRow>): void => {
      const gameStateRow = Array.from(rows)[0];
      if (gameStateRow) {
        const currentTick = Number(gameStateRow.currentTick);
        if (!Number.isSafeInteger(currentTick)) {
          this.logger('tick_value_unsafe', { currentTick: gameStateRow.currentTick });
          return;
        }
        options.onTickUpdate(currentTick);
      }
    };

    const handleTableChange = (ctx: EventContext): void => {
      emitTick(ctx.db.gameState.iter());
    };

    gameStateTable.onInsert(handleTableChange);
    gameStateTable.onDelete(handleTableChange);
    gameStateTable.onUpdate(handleTableChange);

    let listenersRemoved = false;
    const cleanupTableListeners = (): void => {
      if (listenersRemoved) {
        return;
      }
      listenersRemoved = true;
      gameStateTable.removeOnInsert(handleTableChange);
      gameStateTable.removeOnDelete(handleTableChange);
      gameStateTable.removeOnUpdate(handleTableChange);
    };

    // Emit initial tick value
    const currentState = Array.from(connection.db.gameState.iter());
    emitTick(currentState);

    return {
      unsubscribe: () => {
        cleanupTableListeners();
      }
    };
  }
}
